import React from "react";
import { Link } from "react-router-dom";
import { trialCountdown } from "../trialCountdown";
import { BILLING_SETTINGS_PATH } from "../../../routes/paths";
import type { Trial } from "../types/subscriptions.types";
import bannerArtwork from "../../../assets/svg/sidebar/side-bar-banner-img.png";

export interface TrialBannerProps {
  /** The trial as `GET /billing/subscription/` reported it, or `null` while
   *  the read is still out — see the rail, which is the only caller. */
  trial: Trial | null | undefined;
  /**
   * Whether the fortnight is what has run out, which is what decides the
   * wording — the same fact `useSubscriptionGate` names `trialEnded`, and the
   * same one the dashboard's strip is drawn from.
   *
   * It has to be told rather than read off `trial.active`, because a finished
   * trial stays on the record of a workspace that later bought a plan: only the
   * caller knows whether there is a subscription behind it. False while the
   * billing read is still out, so the card claims nothing about an account we
   * do not know yet.
   */
  ended?: boolean;
}

/**
 * The end of the free trial, at the foot of the rail: the last few days of it,
 * and the fact of it once it is gone.
 *
 * Only the sharp end of the fortnight. Up to `TRIAL_URGENT_DAYS` the trial is a
 * fact somebody glances at rather than a deadline, and `TrialMeter`'s quiet bar
 * is the whole of what it owes them; this takes over at `countdown.urgent`, so
 * exactly one of the two is ever drawn. What changes is not the number — that
 * is the same countdown either way — but how loudly it is put and whether a way
 * onto a plan is put beside it.
 *
 * Two ways of saying it, on either side of the end date. The countdown while it
 * runs, and the plain fact of its end after: past the end date a countdown
 * would be describing something that is no longer true, and taking the card
 * away entirely would drop the one way onto a plan the rail carries, at the
 * moment it is most wanted. Same split the dashboard's strip and the locked
 * create buttons make.
 *
 * Nothing at all otherwise: a workspace that never took a trial, one with days
 * of it still to run, and every paying workspace — which is the majority and
 * must not be shown a countdown for a trial it is not on, nor told a trial
 * ended when what it has is a plan.
 *
 * The artwork is a background rather than an `<img>`: it says nothing the words
 * over it do not, and as a background it crops to whatever width the rail has
 * at that breakpoint without the words moving with it. The blue behind it is
 * sampled from the image, so the card reads the same for the moment before it
 * is decoded and if it never arrives at all.
 */
const TrialBanner: React.FC<TrialBannerProps> = ({ trial, ended = false }) => {
  const countdown = trialCountdown(trial);
  const closing = countdown?.urgent ? countdown : null;
  if (!closing && !ended) return null;

  return (
    <section
      aria-label="Free trial"
      style={{
        backgroundImage: `url(${bannerArtwork})`,
        backgroundColor: "#3F5BF0",
      }}
      className="mb-5 3xl:mb-6 overflow-hidden rounded-[6px] bg-cover bg-center px-3 py-3 3xl:px-3.5 3xl:py-3.5"
    >
      {closing ? (
        <>
          {/* "Ends today" is a sentence and the day counts are a number with a
              noun after them, so the two are printed as they come rather than
              split into a figure and a unit — see `trialCountdown`. */}
          <p className="text-[15px] 3xl:text-[17px] font-semibold leading-tight text-white">
            {closing.label}
          </p>
          <p className="mt-0.5 text-[10px] 3xl:text-[12px] leading-tight text-white/80">
            in your free trial
          </p>
        </>
      ) : (
        // One sentence rather than the countdown's heading and line under it:
        // there is no number left to make loud, and what is worth saying is the
        // whole of it. A size down from the countdown so it still sits on two
        // lines in the narrowest rail.
        <p className="text-[14px] 3xl:text-[16px] font-semibold leading-snug text-white">
          Your free <br/> trial has{" "}
          {/* The green the design lifts a word off the blue with — the same one
              the dashboard's strip puts the product's name in. It falls on the
              word that carries the news. */}
          <span className="text-[#52FF89]">ended</span>
        </p>
      )}

      {/* A link rather than a button that navigates, for the reason the
          dashboard's strip gives: it is a screen of the app, so a middle click
          opens it in a tab and the status bar says where it leads.

          Both sides of the fortnight lead to the same tab; only the label
          changes, since "Subscribe now" is an offer and what is owed to
          somebody already shut out is the way back in. */}
      <Link
        to={BILLING_SETTINGS_PATH}
        // Solid white with the label in the blue it stands on — the one thing
        // on the card that is pressed, so it is the one thing filled in.
        className="mt-6 3xl:mt-7 block w-full rounded-[6px] bg-white px-2 py-1.5 3xl:py-2 text-center text-[11px] 3xl:text-[13px] font-medium text-[#3451D9] transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#3F5BF0]"
      >
        {closing ? "Subscribe now" : "Upgrade to Pro"}
      </Link>
    </section>
  );
};

export default TrialBanner;
